import React, { useState } from "react";
import { auth, db } from "../../config/firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  MenuItem, 
  Select, 
  Container,
  CssBaseline,
  FormControl,
  InputLabel
} from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material/styles';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b3b3b3',
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& label.Mui-focused': {
            color: '#90caf9',
          },
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: '#90caf9',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#90caf9',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#90caf9',
          },
        },
      },
    },
  },
});

export default function ClassroomSignupLogin() {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(true);
  const [role, setRole] = useState("college");
  const [collegeId, setCollegeId] = useState("");
  const [collegeName, setCollegeName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      isSignup ? await handleSignup() : await handleLogin();
    } catch (error) {
      console.error("Authentication Error:", error);
      alert(error.message);
    }
  };

  const handleSignup = async () => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    console.log("User signed up with UID:", userId);

    if (role === "college") {
      await registerCollege(userId, email);
    } else {
      await registerTeacherOrStudent(userId);
    }
  };

  const registerCollege = async (userId, email) => {
    const sanitizedCollegeId = collegeName.replace(/\s+/g, "-").toLowerCase();
    console.log("Registering college:", sanitizedCollegeId);

    await setDoc(doc(db, "colleges", sanitizedCollegeId), {
      collegeName,
      adminName,
      adminId: userId,
      createdAt: new Date(),
    });
    await setDoc(doc(db, 'users', adminName), {
      username: adminName,
      uid: userId, 
      email,
      createdAt: new Date()
    });
    setCollegeId(sanitizedCollegeId);
    console.log("College registered successfully:", sanitizedCollegeId);
    navigate(`/${sanitizedCollegeId}`);
  };

  const registerTeacherOrStudent = async (userId) => {
    if (!collegeId.trim()) {
      alert("Please enter a valid College ID.");
      return;
    }

    console.log("Checking if College ID exists:", collegeId);
    const collegeRef = doc(db, "colleges", collegeId);
    const collegeSnap = await getDoc(collegeRef);

    if (!collegeSnap.exists()) {
      alert("College ID not found!");
      return;
    }

    console.log("College ID found, registering user...");
    const collectionPath = role === "teacher" ? `colleges/${collegeId}/teachers` : `colleges/${collegeId}/students`;
    const userCollectionPath = `users`;
    await setDoc(doc(db, collectionPath, userId), { fullName, email, createdAt: new Date() });

    await setDoc(doc(db, userCollectionPath, fullName), { uid: userId, email, username: fullName, createdAt: new Date() });
    console.log("User registered in collection:", fullName, "in", userCollectionPath);

    console.log("User registered successfully:", userId);
    navigate(role === "teacher" ? `/teacher/${collegeId}` : `/student/${collegeId}`);
  };

  const handleLogin = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userId = userCredential.user.uid;
      console.log("User logged in:", userId);

      if (role === "college") {
        await fetchCollegeIdAndNavigate(userId);
      } else {
        await verifyTeacherOrStudent(userId);
      }
    } catch (error) {
      console.error("Login error:", error);
      alert(error.message);
    }
  };

  const fetchCollegeIdAndNavigate = async (userId) => {
    console.log("Fetching college ID for admin:", userId);

    const collegesRef = collection(db, "colleges");
    const q = query(collegesRef, where("adminId", "==", userId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      alert("No college found for this admin account.");
      console.error("No college found for admin:", userId);
      return;
    }

    const collegeDoc = querySnapshot.docs[0];
    const fetchedCollegeId = collegeDoc.id;

    console.log("College ID found:", fetchedCollegeId);
    navigate(`/${fetchedCollegeId}`);
  };

  const verifyTeacherOrStudent = async (userId) => {
    if (!collegeId.trim()) {
      alert("Please enter a valid College ID.");
      return;
    }

    console.log("Verifying user in role:", role, "College ID:", collegeId);
    const collectionPath = role === "teacher" ? `colleges/${collegeId}/teachers` : `colleges/${collegeId}/students`;
    const userRef = doc(db, collectionPath, userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      alert("Invalid role or College ID.");
      console.error("User not found in:", collectionPath);
      return;
    }

    console.log("User verified, navigating...");
    navigate(role === "teacher" ? `/teacher/${collegeId}` : `/student/${collegeId}`);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Container 
        maxWidth="sm" 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4
        }}
      >
        <Box 
          sx={{
            width: '100%',
            p: 4,
            borderRadius: 2,
            boxShadow: 3,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              mb: 3, 
              fontWeight: 600,
              textAlign: 'center',
              color: 'primary.main'
            }}
          >
            {isSignup ? "Create Account" : "Welcome Back"}
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value)}
              sx={{ textAlign: 'left' }}
            >
              <MenuItem value="college">College Admin</MenuItem>
              <MenuItem value="teacher">Teacher</MenuItem>
              <MenuItem value="student">Student</MenuItem>
            </Select>
          </FormControl>

          <Box 
            component="form" 
            onSubmit={handleAuth}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {isSignup && role === "college" && (
              <>
                <TextField 
                  fullWidth 
                  label="College Name" 
                  value={collegeName} 
                  onChange={(e) => setCollegeName(e.target.value)} 
                  required 
                  variant="outlined"
                />
                <TextField 
                  fullWidth 
                  label="Admin Name" 
                  value={adminName} 
                  onChange={(e) => setAdminName(e.target.value)} 
                  required 
                  variant="outlined"
                />
              </>
            )}

            {role !== "college" && (
              <TextField 
                fullWidth 
                label="College ID" 
                value={collegeId} 
                onChange={(e) => setCollegeId(e.target.value)} 
                required 
                variant="outlined"
              />
            )}

            {isSignup && role !== "college" && (
              <TextField 
                fullWidth 
                label="Full Name" 
                value={fullName} 
                onChange={(e) => setFullName(e.target.value)} 
                required 
                variant="outlined"
              />
            )}

            <TextField 
              fullWidth 
              label="Email" 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              variant="outlined"
            />
            <TextField 
              fullWidth 
              label="Password" 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              variant="outlined"
            />

            <Button 
              type="submit" 
              variant="contained" 
              size="large"
              fullWidth 
              sx={{
                mt: 2,
                py: 1.5,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'none',
                fontSize: '1rem'
              }}
            >
              {isSignup ? "Create Account" : "Sign In"}
            </Button>
          </Box>

          <Typography 
            variant="body2" 
            sx={{ 
              mt: 3, 
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            {isSignup ? "Already have an account?" : "Don't have an account?"}
            <Button 
              onClick={() => setIsSignup(!isSignup)}
              sx={{
                ml: 1,
                color: 'primary.main',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              {isSignup ? "Sign In" : "Sign Up"}
            </Button>
          </Typography>
        </Box>
      </Container>
    </ThemeProvider>
  );
}